import clsx from 'clsx';
import { forwardRef } from 'react';

import { dividerCSS } from './Divider.sty.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { type DataTestId } from '../../core/types/data-props.js';
import { type StylingProps } from '../../core/types/styling-props.js';
import { colorUtilsCSS } from '../../core/utils/colorUtils.sty.js';

const COMPONENT_NAME = 'Divider';

// #region Typings

/** @public
 * Divider component props.
 */
export interface DividerProps
  extends StylingProps,
    DataTestId,
    BehaviorTrackingProps {
  /**
   * Orientation of the Divider component.
   * @defaultValue 'horizontal'
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Control the HTML tag used for rendering the Divider.
   * @defaultValue 'div'
   */
  as?: 'hr' | 'div';
  /**
   * Indicate whether the Divider is used inside a Flex component to apply the right styling.
   * If set to false, make sure to have an explicit width/height style set on a parent HTML tag.
   * @defaultValue true
   */
  flexItem?: boolean;
  /**
   * The color of the Divider.
   * @defaultValue 'neutral'
   */
  color?: 'primary' | 'neutral' | 'success' | 'warning' | 'critical';
  /**
   * The visual style of the Divider.
   * @defaultValue 'default'
   */
  variant?: 'default' | 'accent';
}
// #endregion

/**
 * The `Divider` component visually separates groups of content.
 * @public
 */
export const Divider = /* @__PURE__ */ forwardRef<
  HTMLDivElement | HTMLHRElement,
  DividerProps
>((props, ref) => {
  const {
    orientation = 'horizontal',
    flexItem = true,
    as = 'div',
    color = 'neutral',
    variant = 'default',
    'data-testid': dataTestId,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const DividerTag = as;

  return (
    <DividerTag
      role="separator"
      aria-orientation={orientation === 'vertical' ? orientation : undefined}
      // @ts-expect-error ref can't be div and hr here
      ref={ref}
      data-testid={dataTestId}
      className={clsx(
        colorUtilsCSS({ color, variant }),
        dividerCSS({ orientation, flexItem }),
        consumerClassName,
      )}
      style={consumerStyle}
      {...restProps}
      {...behavioralTrackingProps}
      data-dt-component={COMPONENT_NAME}
    />
  );
});

(Divider as typeof Divider & { displayName: string }).displayName = 'Divider';
